<template>
  <!--Note: Hide/show is owned by the parent since the buttons firing this live there-->
  <SrpModal className="create-collab-form" :isVisible="visible" @close="onClose" :isClosable="false" size="large" maxHeight="calc(100dvh - 120px)" isWithScroll :isWithBottomGap="false">
    <template #content>
      <div class="modal-description-wrap">
        <div class="modal-description">
          <StepIntroMain
            v-if="currentStep === 0"
            :collabInput="collabInput"
            :customerId="communityId"
            @changeStep="
              stepNumber => {
                changeStep(stepNumber);
              }
            "
            @mount="
              () => {
                collabIdToClone = null; //it's needed to clear the id when user get's back to the intro screen and selects any other option instead of clone
              }
            "
            :introSteps="introSteps"
            :draftCollabs="draftCollabs"
            :collabSummaries="collabSummariesRecentFirst"
            :plannedCollabs="plannedCollabs"
            :isLoadingCollabSummaries="isLoadingCollabSummaries"
            :isLoadingPlannedCollabs="isLoadingPlannedCollabs"
          />

          <!--<StepsSummary v-if="step === 0" />-->

          <StepIntroFresh v-if="currentStep === introSteps.freshStep" :collabInput="collabInput" @changeStep="() => changeStep(1)" />
          <StepIntroDraft
            v-if="currentStep === 0.2"
            :customerId="communityId"
            @click:getStarted="
              collabId => {
                $emit('editCollabDraft', collabId);
                currentStep = 1;
              }
            "
            :draftCollabs="draftCollabs"
          />
          <StepIntroInspiration
            v-if="currentStep === 0.3"
            :communityId="communityId"
            @click:getStarted="
              collabSuggestionId => {
                setCollabSuggestionId(collabSuggestionId);
                currentStep = 1;
              }
            "
          />
          <StepIntroPlanned
            v-if="currentStep === 0.4"
            :communityId="communityId"
            @click:getStarted="
              plannedCollab => {
                setPlannedCollabFields(plannedCollab);
                currentStep = 1;
              }
            "
            :plannedCollabs="plannedCollabs"
          />
          <StepIntroClone
            v-if="currentStep === 0.5"
            @click:cloneCollab="
              id => {
                collabIdToClone = id;
                changeStep(1);
              }
            "
            :collabSummaries="collabSummariesRecentFirst"
          />

          <StepTiming v-if="currentStep === createStep" :collabInput="collabInput" :communityId="communityId" :collabDateResetCounter="collabDateResetCounter" :errors="errors" />

          <StepLocationsNAdventures
            v-if="currentStep === locationsNAdventuresStep"
            v-model:requestedLocationCount="collabInput.requestedLocationCount"
            v-model:requestedAdventureCount="collabInput.requestedAdventureCount"
            :customerOwnedAdventures="collabInput.collaboration?.customerOwnedAdventures"
            :themeType="collabInput.themeType"
            :errors="errors"
          />

          <StepLocations v-if="currentStep === locationsAndActivitiesStep" :collabInput="collabInput" :validationResult="validationResult" :clearValidation="clearValidation" :errors="errors" />

          <StepThemes v-if="currentStep === themesStep" :collabInput="collabInput" :errors="errors" />

          <StepLodging v-if="currentStep === lodgingStep" :collabInput="collabInput" :collabRemainingCounts="collabRemainingCounts" :errors="errors" />

          <StepGearNMaterial v-if="currentStep === gearNMaterialStep" :collabInput="collabInput" :errors="errors" />

          <StepBoundaries v-if="currentStep === boundariesStep" :collabInput="collabInput" :errors="errors" />

          <StepName v-if="currentStep === lastStep" :collabInput="collabInput" :errors="errors" @jumpToCompedActivitiesStep="currentStep = locationsAndActivitiesStep" />
        </div>
      </div>

      <StepsProgressBar v-if="currentStep >= 1" class="create-collab-form__steps-progressbar" :currentStep="currentStep" :numberOfSteps="lastStep" />

      <!--Errors-->
      <div v-if="errors.length > 0" style="color: red; margin-bottom: 15px">
        <h5 style="margin: 10px 0 0 0">Required Fields</h5>
        <div v-for="(error, i) in errors" :key="'error' + i">- {{ error }}</div>
      </div>
    </template>

    <!--Hide the actions panel if it's a brand new collab (since choosing a themeType moves to the next step)-->
    <template #footer v-if="currentStep > 0 || collabInput?.id || plannedCollab">
      <label v-if="isSuperOrSalesUser && errors?.length > 0" style="width: auto">
        <input type="checkbox" v-model="superOverrideValidation" style="display: inline; width: auto" />
        skip validation (super)
      </label>
      <h3 style="margin: 0 15px 0 0; display: inline">{{ isSaving ? "Saving..." : "" }} {{ doneSavingMessage ? "Saved" : "" }}</h3>
      <SrpButton
        v-if="currentStep >= 1 || (currentStep > 0 && currentStep < 1 && !hasCollabBeenCreated)"
        :isDisabled="isSaving"
        color="gray"
        @click="
          () => {
            if (currentStep === 1 && hasCollabBeenCreated) {
              // They can only edit the theme
              changeStep(introSteps.freshStep);
            } else if (isCurrentStepIntroButNotMain) {
              // They can choose a create flow
              currentStep = 0;
            } else {
              changeStep(-1);
            }
          }
        "
        fill="outlined"
        style="margin-right: 10px"
      >
        Back
      </SrpButton>

      <template v-if="!isCurrentStepIntroButNotMain">
        <SrpButton v-if="currentStep !== lastStep" :isDisabled="isSaving" @click="changeStep(1)">
          {{ currentStep === 0 && collabInput?.id == null && plannedCollab == null ? "Let's Go!" : "Next" }}
        </SrpButton>
        <SrpButton v-else @click="changeStep(1)">Save</SrpButton>
      </template>
    </template>
  </SrpModal>
</template>

<script lang="ts">
/* eslint vue/no-mutating-props: 0 */
import axios from "axios";
import CollabInputValidator from "@logic/CollabInputValidator";
import CollabStatuses from "@logic/CollabStatuses";
import { defineComponent, inject } from "vue";
import { defineIsCollabDraft } from "@logic/CollabsMiscellaneous";

// Contracts
import CollabConsts, { CollabInput, CreatorCollabSummary, CustomerCollabRemainingCounts, ThemeType, themeTypes } from "@contracts/collab";
import { CollabCreationSteps } from "@constants/collab";
import { CollabInputValidationError } from "@logic/CollabInputValidator";
import { PlannedCollab } from "@contracts/plannedCollabs";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
// intro steps
import StepIntroMain from "./IntroSteps/StepIntroMain.vue";
import StepIntroFresh from "./IntroSteps/StepIntroFresh.vue";
import StepIntroDraft from "./IntroSteps/StepIntroDraft.vue";
import StepIntroInspiration from "./IntroSteps/StepIntroInspiration.vue";
import StepIntroPlanned from "./IntroSteps/StepIntroPlanned.vue";
import StepIntroClone from "./IntroSteps/StepIntroClone.vue";
// steps
import StepsSummary from "./StepsSummary.vue";
import StepThemes from "./StepThemes.vue";
import StepTiming from "./StepTiming.vue";
import StepLocations from "./StepLocations.vue";
import StepLocationsNAdventures from "./StepLocationsNAdventures.vue";
import StepLodging from "./StepLodging.vue";
import StepGearNMaterial from "./StepGearNMaterial.vue";
import StepBoundaries from "./StepBoundaries.vue";
import StepName from "./StepName.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import { useUserProfileStore } from "@stores/userProfileStore";
import { mapState } from "pinia";
import StepsProgressBar from "@components/StepsProgressBar.vue";

export default defineComponent({
  name: "CreateCollab",

  components: {
    StepsProgressBar,
    SrpButton,
    SrpModal,
    // intro steps
    StepIntroMain,
    StepIntroFresh,
    StepIntroDraft,
    StepIntroInspiration,
    StepIntroPlanned,
    StepIntroClone,
    // steps
    StepsSummary,
    StepThemes,
    StepTiming,
    StepLocations,
    StepLocationsNAdventures,
    StepLodging,
    StepGearNMaterial,
    StepBoundaries,
    StepName,
  },

  props: {
    communityId: { type: String, required: true },
    // Note: The Parent must default this if new or set if editing an existing.
    collabInput: { type: Object as () => CollabInput | null, required: false, default: null },
    collabRemainingCounts: { type: Object as () => CustomerCollabRemainingCounts | null, required: false, default: null },
    visible: { type: Boolean, required: true },
    isLoadingCollabSummaries: { type: Boolean, required: false },
    isLoadingPlannedCollabs: { type: Boolean, required: false },
    // Specific Create Flow inputs
    plannedCollab: { type: Object as () => PlannedCollab | null, required: false, default: null },
    collabSuggestionId: { type: String, required: false, default: null },
    createdForCreatorId: { type: String, required: false, default: null },

    defaultTheme: { type: String, required: false, default: null },
    isMainIntroStepAvailable: { type: Boolean, required: false, default: true }, // prevents getting to the MainIntro step when the modal is in "Edit collab" mode
    plannedCollabs: { type: Array as () => Array<PlannedCollab>, required: false, default: () => [] },
    collabs: { type: Array as () => Array<CollabInput>, required: false, default: () => [] },
    collabSummaries: { type: Array as () => Array<CreatorCollabSummary>, required: false, default: () => [] },
  },

  emits: ["close", "collabCreated", "setCollabSuggestionId", "editCollabDraft"],

  data() {
    return {
      globalLog: inject("globalLog") as any,

      currentStep: 0,

      introSteps: {
        freshStep: 0.1,
        draftStep: 0.2,
        inspirationStep: 0.3,
        plannedStep: 0.4,
        cloneStep: 0.5,
      },

      firstMainStep: CollabCreationSteps.Timing,
      createStep: CollabCreationSteps.Timing,
      // Moved this step earlier in the flow now
      locationsNAdventuresStep: CollabCreationSteps.LocationsNAdventures,
      locationsAndActivitiesStep: CollabCreationSteps.LocationsAndActivities,
      themesStep: CollabCreationSteps.Themes,
      lodgingStep: CollabCreationSteps.Lodging,
      gearNMaterialStep: CollabCreationSteps.GearNMaterial,
      boundariesStep: CollabCreationSteps.Boundaries,
      lastStep: CollabCreationSteps.Name,

      isSaving: false,
      isDirty: false,
      doneSavingMessage: false,

      collabIdToClone: null as string | null,

      collabDateResetCounter: 1,

      CollabInviteExpirationInDays: CollabConsts.CollabInviteExpirationInDays,
      DaysCreatorHasToCreateAdventures: CollabConsts.DaysCreatorHasToCreateAdventures,

      errors: [],
      superOverrideValidation: false,
      validationResult: null as CollabInputValidationError | null,
      version: 3, // Note: Just used for creating collabs, otherwise collab.version is used
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["isSuperOrSalesUser"]),
    isCurrentStepIntroButNotMain() {
      return this.currentStep > 0 && this.currentStep < this.firstMainStep;
    },
    hasCollabBeenCreated(): boolean {
      return this.collabInput?.id?.length > 0;
    },
    draftCollabs(): Array<CollabInput> {
      return this.collabs.filter(c => defineIsCollabDraft(c));
    },

    collabSummariesRecentFirst(): Array<CreatorCollabSummary> {
      return this.collabSummaries.slice().sort((a, b) => {
        // nulls sort after anything else
        if (a.completedDate === null) return 1;
        if (b.completedDate === null) return -1;
        return b.completedDate - a.completedDate;
      });
    },
  },

  watch: {
    // Need an isDirty check so we save on next/back/close
    collabInput: {
      handler(newVal, oldVal) {
        if (oldVal != null) {
          if (oldVal.id != newVal.id) {
            this.globalLog.info("Switching Collabs, resetting isDirty");
            this.setIsDirty(false);
          } else {
            this.setIsDirty(true);
          }
        }
      },
      deep: true,
    },
    visible(value) {
      if (!value) return;
      this.initialize();
    },
  },

  async mounted() {
    this.initialize();
  },

  methods: {
    initialize() {
      // Check if this is the edit flow
      if (this.collabInput?.id?.length > 0) {
        this.jumpToStep(this.createStep);
      } else {
        // --- In one of the Create flows ---
        if (this.collabSuggestionId) {
          this.changeStep(this.createStep);
        } else if (this.plannedCollab) {
          this.setPlannedCollabFields(this.plannedCollab);
          this.changeStep(this.createStep);
        } else if (this.defaultTheme && themeTypes.includes(this.defaultTheme as ThemeType)) {
          this.collabInput.themeType = this.defaultTheme as ThemeType;
          this.changeStep(this.createStep);
        }
      }
    },
    setCollabSuggestionId(collabSuggestionId: string) {
      this.$emit("setCollabSuggestionId", collabSuggestionId);
    },
    setPlannedCollabFields(plannedCollab: PlannedCollab) {
      // If the name is still "Creator Visit 1/2/3", then clear it out
      if (
        plannedCollab.name?.startsWith("Creator Visit ") === true &&
        // @ts-ignore
        !isNaN(plannedCollab.name.trim().slice(-1))
      ) {
        plannedCollab.name = null;
      }
      // Set the fields
      this.collabInput.themeType = plannedCollab.themeType;
      this.collabInput.name = plannedCollab.name;
      this.collabInput.themes = plannedCollab.notes;
      this.collabInput.requiredAttractions = plannedCollab.requiredLocations;
      this.collabInput.suggestedAttractions = plannedCollab.suggestedLocations;
      if (plannedCollab.preferredDateRangeStart && plannedCollab.preferredDateRangeEnd) {
        this.collabInput.preferredDateRangeStart = plannedCollab.preferredDateRangeStart;
        this.collabInput.preferredDateRangeEnd = plannedCollab.preferredDateRangeEnd;
      }
    },
    async createNewCollab(): Promise<boolean> {
      if (!this.validate(this.collabInput, this.createStep)) return;

      this.isSaving = true;
      // Server generates the Id
      this.collabInput.version = this.version;

      const urlSearchParams = new URLSearchParams();
      if (this.collabIdToClone) {
        urlSearchParams.append("collabInputIdToClone", String(this.collabIdToClone));
      }
      if (this.collabSuggestionId) {
        urlSearchParams.append("createFromCollabSuggestionId", String(this.collabSuggestionId));
      }

      if (this.createdForCreatorId) {
        this.collabInput.createdForCreatorId = this.createdForCreatorId;
      }
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/collabs/${this.communityId}?` + urlSearchParams.toString(), this.collabInput);
      // this.newCollab = response.data;
      // Tell the parent that the collab was created
      this.$emit("collabCreated", response.data);

      if (this.collabIdToClone) this.globalLog.info(`Cloned! CollabInputId=${this.collabInput.id}`);
      else this.globalLog.info(`Created! CollabInputId=${this.collabInput.id}`);

      this.collabIdToClone = null;

      this.$nextTick(() => {
        this.setIsDirty(false);
      });
      // Gets the CollabDatePicker to reset it's local data
      this.collabDateResetCounter++;
      this.isSaving = false;
      this.doneSavingMessage = true;
      setTimeout(() => {
        this.doneSavingMessage = false;
      }, 1500);
      return true;
    },
    async save(validate: boolean, stepToValidate: number | null, notify: boolean): Promise<boolean> {
      if (this.createdForCreatorId) {
        this.collabInput.createdForCreatorId = this.createdForCreatorId;
      }
      // Validation
      if (validate) {
        if (!this.validate(this.collabInput, stepToValidate)) return false;
      }

      this.isSaving = true;
      // Note: We pass a notify param to reduce the volume of update notifications we send ourselves
      // Always notify if it's already been sent to creators
      if (this.isPastInputStatus()) notify = true;

      this.cleanupCollabFieldsBeforeSave(this.collabInput);
      const uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}?notify=${notify}`;
      const response = await axios.put(uri, this.collabInput);
      this.globalLog.info(`Saved! CollabInputId=${this.collabInput.id}`);
      this.collabInput._etag = response.data._etag;
      this.setIsDirty(false);
      this.isSaving = false;
      this.doneSavingMessage = true;

      setTimeout(() => {
        this.doneSavingMessage = false;
      }, 1500);
      return true;
    },
    cleanupCollabFieldsBeforeSave(collab: CollabInput) {
      // Note: Decimals in the estimated value fields causes failures since they're integers server-side and the default server validation is surprisingly strict.
      if (collab.compedActivitiesEstimatedValue) {
        collab.compedActivitiesEstimatedValue = Math.trunc(collab.compedActivitiesEstimatedValue);
      }
      if (collab.hotelEstimatedValue) {
        collab.hotelEstimatedValue = Math.trunc(collab.hotelEstimatedValue);
      }
    },
    // Jumps to a specific step (used by the Edit button to jump into the flow)
    async jumpToStep(step: number) {
      this.currentStep = step;
    },
    shouldSeeHotelStep(): boolean {
      return this.collabRemainingCounts?.showLodgingStep ?? true;
    },
    async defaultHotelCoverageUsed() {
      // console.info(`Hotel Coverage=${this.collabInput.hotelCoverageUsed}`);
      if (!this.collabInput.hotelCoverageUsed || this.collabInput.hotelCoverageUsed === "Unset") {
        console.info(`${this.collabRemainingCounts.collabHotelsGrandfathered} grandfathered and ${this.collabRemainingCounts.collabHotelsPaidRemaining} addons `);
        if (this.collabRemainingCounts.collabHotelsGrandfathered > 0) {
          this.collabInput.hotelCoverageUsed = "Grandfathered";
        } else if (this.collabRemainingCounts.allRemaininingHavePaidCoverage === true) {
          this.collabInput.hotelCoverageUsed = "PaidAddOn";
          // Also default this field since it was always set previously (hard to say if leaving it null would cause problems...)
          this.collabInput.hotelNightsCovered = 0;
        } else {
          // Let the customer select
        }
      }
    },
    // Moves back and forth steps (used by the main buttons) - SAVES if dirty
    async changeStep(value: number) {
      // If the value is an Intro step ( > 0 && < 1) but not the Main intro step ( 0 )
      if (value > 0 && value < 1) {
        this.currentStep = value;
        return;
      }

      // Removes decimal part for not "intro" values (intro values are those which > 0 && < 1)
      if (!Number.isInteger(this.currentStep)) {
        this.currentStep = Math.floor(this.currentStep);
      }

      // If the user hits Next always validate
      if (value >= this.firstMainStep) {
        if (!this.validate(this.collabInput, this.currentStep)) return;
      }
      if (value < 0) {
        this.clearValidation();
      }

      // Check if they should see the hotel step
      if (this.currentStep + value === this.lodgingStep) {
        await this.defaultHotelCoverageUsed();
      }
      let shouldSeeHotelStep = this.shouldSeeHotelStep();

      // Ensure the estimate fields are unset of they don't apply (based on the UI logic above)
      if (this.currentStep === this.locationsAndActivitiesStep) {
        if (this.collabInput.requestingBigTicketItems === false && this.collabInput.compedActivitiesTags.length === 0) {
          this.collabInput.compedActivitiesEstimatedValue = null;
          this.collabInput.bigTicketItemBookinNotes = null; // This also hides if no comps tags are selected
        }
      } else if (this.currentStep === this.lodgingStep) {
        if (this.collabInput.hotelNightsCovered === 0) {
          this.collabInput.hotelEstimatedValue = null;
        }
      }

      const needToCreate = !(this.collabInput.id?.length > 0);
      // Each steps saves except the first intro slide
      if (this.currentStep !== 0 && this.isDirty) {
        // If we're in the create flow and the collab has no Id then create it, otherwise update it
        if (needToCreate) {
          // Create
          await this.createNewCollab();
        } else {
          // Save
          // notify=false here since other code paths cover create/done and we don't want to notify on each intermediate save.
          await this.save(false, null, false); // Validated above()
        }
      }
      if (this.currentStep === this.lastStep && value > 0) {
        await this.onClose();
      } else if (this.currentStep > 0 || this.currentStep < this.lastStep) {
        this.currentStep = this.currentStep + value;
        // Check if we should skip the hotel stop
        if (this.currentStep === this.lodgingStep && !shouldSeeHotelStep) {
          this.currentStep = this.currentStep + value;
        }
      }

      // reset this field
      this.superOverrideValidation = false;
    },
    setIsDirty(dirty: boolean) {
      // this.globalLog.info(`isDirty=${dirty}`);
      this.isDirty = dirty;
    },
    validate(collabToValidate: CollabInput, stepToValidation: number): boolean {
      // Note: The wizard validates step by step as the user progresses through the flow
      this.validationResult = CollabInputValidator.validateCollabInputs(collabToValidate, stepToValidation, this.isSuperOrSalesUser, this.superOverrideValidation);
      this.errors = this.validationResult.errors;
      return this.errors.length === 0;
    },
    clearValidation() {
      this.validationResult = null;
      this.errors = [];
    },
    async onClose() {
      const isPostedToCreators = this.isPastInputStatus();
      // We need to validate if they've submitted this collab, otherwise we let them close without passing validation
      if (isPostedToCreators) {
        if (!this.validate(this.collabInput, null)) {
          this.$emit("close");
          return;
        }
      }
      // Save on close if there are changes (otherwise it'll look like they're saved but they might not have been)
      if (this.isDirty && this.collabInput.id?.length > 0) {
        await this.save(isPostedToCreators, null, true);
      }
      this.errors = [];
      this.$nextTick(() => {
        this.setIsDirty(false);
      });
      this.collabDateResetCounter = 1;

      // Delay is needed to avoid the form from switching to the first slide before the modal closing animation ends
      setTimeout(() => (this.currentStep = 0), 400);
      this.$emit("close");
    },
    isPastInputStatus(): boolean {
      return this.collabInput?.collaboration && this.collabInput.collaboration.status !== CollabStatuses.CommunityInputStatusId;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

.modal-description-wrap {
  width: 100%;
  max-width: calc(100vw - 50px);
}

// Create collab form =========================================================
:global(.create-collab-form .srp-modal__content) {
  padding-bottom: 0 !important;
}

:global(.create-collab-form__steps-progressbar) {
  margin-bottom: 25px;
}

.themes-dropdown {
  color: #000 !important;
  font-weight: normal;
  .ui.label {
    color: #000 !important;
    font-weight: normal;
  }
}
//Override the default semantic min-height
.ui.form textarea {
  min-height: 60px;
  //Default height (override inline on some)
  height: 80px;
}
.step-counter {
  margin-bottom: -1rem;
}
.ui.header.all-set > i {
  font-size: 2rem !important;
}
.all-set-container {
  border-radius: 8px;
  padding: 1rem;
  background-color: $brand-color-lightest;
  text-align: center;
}
.intro-text {
  margin-top: 4px;
  margin-bottom: 0.5rem;
}
.modal-description {
  width: calc(100% + 120px);
  padding: 0 1rem 20px 120px;
  margin-left: -120px;
  box-sizing: border-box;
  overflow-y: auto;
  // -ms-overflow-style: none;  /* IE and Edge */
  // scrollbar-width: none;  /* Firefox */

  &::-webkit-scrollbar-track {
    background: white;
  }
}
.ui.modal > .content {
  padding-right: 3rem !important;
  border-radius: 6px;
}
//needed to override the default label styling
.time-label {
  color: #058587 !important;
  font-weight: 500 !important;
  background-color: #05858700;
  font-size: 0.9em;
}
.current-step {
  background-color: $brand-color-lightest;
}
.ui.segment span.ui.header span.number {
  font-weight: 400;
  margin-right: 1rem;
}

.required-field {
  color: red;
  font-weight: bold;
  margin-left: 5px;
  font-size: 13px;
}
</style>
